import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Wand2, Users, AlertTriangle } from "lucide-react";
import { useDepartments } from "@/contexts/DepartmentsContext";

/**
 * "Fix unassigned" — manager-only diagnostic modal for Reports.
 *
 * Two buckets to resolve:
 *   1. Items with no department → per-row dept picker + Save.
 *   2. Historical OUT movements taken before dept was captured, whose item
 *      NOW has a dept → one-click "backfill from item's dept" button.
 *
 * Orphaned movements (item was deleted) can't be backfilled — surfaced as
 * an info note only.
 */
const FixUnassignedDialog = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const { departments } = useDepartments();

  const load = async () => {
    try {
      const { data } = await api.get("/reports/unassigned");
      setData(data);
    } catch {
      toast.error("Could not load unassigned diagnostics");
    }
  };
  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const assignItem = async (id, deptName) => {
    if (!deptName) return;
    setSavingId(id);
    try {
      await api.put(`/stock/${id}`, {
        department: deptName,
        departments: [deptName],
      });
      toast.success("Item assigned");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const backfillMovements = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports/unassigned/backfill-movements");
      toast.success(
        `Backfilled ${data.fixed} movement(s)`,
        data.skipped_orphan
          ? {
              description: `${data.skipped_orphan} orphan(s) (item deleted) skipped.`,
            }
          : undefined,
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  const purgeOrphans = async () => {
    if (
      !window.confirm(
        "Soft-delete all orphan movements (item was deleted)? They will disappear from Reports but remain restorable in History → Show deleted.",
      )
    )
      return;
    setBusy(true);
    try {
      const { data } = await api.post("/reports/unassigned/purge-orphan-movements");
      toast.success(`Purged ${data.purged} orphan movement(s)`, {
        description: "Restorable via History → Show deleted → pencil → Restore.",
      });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  const items = data?.items || [];
  const fixableCount = data?.movements_without_dept_but_item_has || 0;
  const orphanCount = data?.movements_orphan || 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="fix-unassigned-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(720px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Reports · diagnostics</div>
            <h3 className="font-display text-2xl">Fix unassigned</h3>
          </div>
          <button
            data-testid="fix-unassigned-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5">
          {/* Historical movements bucket */}
          <div className="border-2 border-black rounded-xl p-4 bg-[#F4F8FB]">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} strokeWidth={2.5} className="text-[#5B3A99]" />
              <div className="font-display text-lg">
                {fixableCount} historical movement{fixableCount === 1 ? "" : "s"} without a dept
              </div>
            </div>
            {fixableCount > 0 ? (
              <>
                <p className="text-xs text-[#607087] mb-3">
                  These OUT movements were taken before a department was
                  captured. Their parent item now has a department set — one
                  tap will backfill each movement with its item&apos;s dept and
                  the &ldquo;Unassigned&rdquo; slice on the Consumption chart
                  will drop accordingly.
                </p>
                <button
                  data-testid="backfill-movements-btn"
                  onClick={backfillMovements}
                  disabled={busy}
                  className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-2 text-sm"
                >
                  <Wand2 size={14} strokeWidth={2.5} />
                  {busy ? "Backfilling…" : `Backfill ${fixableCount} movement${fixableCount === 1 ? "" : "s"}`}
                </button>
              </>
            ) : (
              <p className="text-xs text-[#4DA86A] font-semibold">
                All historical movements already have a department. ✓
              </p>
            )}
          </div>

          {/* Items bucket */}
          <div>
            <div className="font-display text-lg mb-2">
              {items.length} item{items.length === 1 ? "" : "s"} with no department
            </div>
            {items.length === 0 ? (
              <div className="text-xs text-[#4DA86A] font-semibold px-3 py-4 border-2 border-dashed border-[#4DA86A] rounded-xl bg-[#EAF9EF]">
                Every item has a department assigned. ✓
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {items.map((it) => (
                  <div
                    key={it.id}
                    data-testid={`unassigned-item-${it.id}`}
                    className="nb-card-sm p-3 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-display truncate">{it.name}</div>
                      <div className="text-xs text-[#607087]">
                        {it.type} · qty {it.current_qty ?? 0} {it.unit}
                      </div>
                    </div>
                    <select
                      data-testid={`unassigned-item-select-${it.id}`}
                      defaultValue=""
                      disabled={savingId === it.id}
                      onChange={(e) => assignItem(it.id, e.target.value)}
                      className="nb-input px-2 py-1.5 text-sm"
                    >
                      <option value="" disabled>
                        {savingId === it.id ? "Saving…" : "Pick a dept"}
                      </option>
                      {(departments || []).map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Orphan note */}
          {orphanCount > 0 && (
            <div className="text-xs text-[#5B4500] px-3 py-2 rounded-md border-2 border-[#C7A43B] bg-[#FFF7DB] flex items-start gap-2">
              <AlertTriangle
                size={14}
                strokeWidth={2.5}
                className="mt-0.5 shrink-0"
              />
              <div className="flex-1">
                {orphanCount} movement{orphanCount === 1 ? "" : "s"} reference
                stock items that have been deleted — they can&apos;t be
                backfilled automatically. Purge them below (soft-delete;
                restorable from History) to clear the &ldquo;Unassigned&rdquo;
                slice.
                <div className="mt-2">
                  <button
                    data-testid="purge-orphans-btn"
                    onClick={purgeOrphans}
                    disabled={busy}
                    className="nb-btn nb-btn-danger px-3 py-1.5 text-xs flex items-center gap-1.5"
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} />
                    {busy
                      ? "Purging…"
                      : `Purge ${orphanCount} orphan movement${orphanCount === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FixUnassignedDialog;
